/**
 * Copyright (c) 2026 Zoldier & LCN Softwares All rights reserved.
 *
 * This software is the confidential and proprietary information of Zoldier & LCN Software.
 * You shall not disclose such confidential information and shall use it only
 * in accordance with the terms of the license agreement you entered into with
 * Zoldier & LCN Software.
 */

import { useTranslation } from 'react-i18next';
import type { SupportedLocale } from '../../locales';

/**
 * Locale management hook backed by i18next (NO Redux).
 *
 * Wraps `i18next.changeLanguage` and exposes the active locale plus simple
 * helpers for switching language. The locale is automatically persisted to
 * localStorage by i18next's LanguageDetector under the `zia-locale` key.
 *
 * The raw i18next language string (e.g., `"en-US"`) is normalized to a
 * `SupportedLocale` by taking only the primary subtag. Any unrecognized
 * language code falls back to `"en"`. This hook does not cause any network
 * requests or side effects beyond what i18next already manages internally.
 *
 * @returns An object containing the active `locale`, the `isEnglish` boolean
 *   convenience flag, a `setLocale` function to switch to a specific locale,
 *   and a `toggleLocale` function to flip between `"en"` and `"es"`.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export function useLocale() {
  const { i18n } = useTranslation();

  const rawLocale = i18n.language?.split('-')[0] ?? 'en';
  const locale: SupportedLocale = rawLocale === 'es' ? 'es' : 'en';
  const isEnglish = locale === 'en';

  const setLocale = (next: SupportedLocale): void => {
    void i18n.changeLanguage(next);
  };

  const toggleLocale = (): void => {
    setLocale(isEnglish ? 'es' : 'en');
  };

  return { locale, isEnglish, setLocale, toggleLocale };
}
